"use client";

import { c as _c } from "react/compiler-runtime";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getDefaultQuickVoteAmount, normalizeQuickVoteAmount } from "@/hooks/memesQuickVote.helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useMemesQuickVoteQueue } from "@/hooks/useMemesQuickVoteQueue";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MemesQuickVoteControls from "./MemesQuickVoteControls";
import MemesQuickVoteDialogSkeleton from "./MemesQuickVoteDialogSkeleton";
import MemesQuickVotePreview from "./MemesQuickVotePreview";
const QUICK_VOTE_MOBILE_QUERY = "(max-width: 767px)";
const QUICK_VOTE_BAR_FEEDBACK_DURATION_MS = 650;
type VoteFeedbackSource = "custom-submit" | "quick-amount";
type TimeoutHandle = ReturnType<typeof globalThis.setTimeout>;
interface MemesQuickVoteDialogProps {
  readonly isOpen: boolean;
  readonly sessionId: number;
  readonly onClose: () => void;
}
interface MemesQuickVoteDialogContentProps {
  readonly activeDrop: NonNullable<ReturnType<typeof useMemesQuickVoteQueue>["activeDrop"]>;
  readonly isMobile: boolean;
  readonly latestUsedAmount: number | null;
  readonly onClose: () => void;
  readonly remainingCount: number;
  readonly recentAmounts: number[];
  readonly submitVote: ReturnType<typeof useMemesQuickVoteQueue>["submitVote"];
  readonly skipDrop: ReturnType<typeof useMemesQuickVoteQueue>["skipDrop"];
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
}
function MemesQuickVoteDialogContent(t0) {
  const $ = _c(62);
  const {
    activeDrop,
    isMobile,
    latestUsedAmount,
    onClose,
    remainingCount,
    recentAmounts,
    submitVote,
    skipDrop,
    uncastPower,
    votingLabel
  } = t0;
  const maxRating = activeDrop.context_profile_context?.max_rating ?? 0;
  let t1;
  if ($[0] !== maxRating) {
    t1 = maxRating > 0 ? getDefaultQuickVoteAmount(maxRating) : 1;
    $[0] = maxRating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultAmount = t1;
  let t2;
  if ($[2] !== defaultAmount) {
    t2 = () => `${defaultAmount}`;
    $[2] = defaultAmount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [customValue, setCustomValue] = useState(t2);
  let t3;
  if ($[4] !== recentAmounts.length) {
    t3 = () => recentAmounts.length === 0;
    $[4] = recentAmounts.length;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [isCustomOpen, setIsCustomOpen] = useState(t3);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [voteFeedback, setVoteFeedback] = useState(null);
  const barVoteTimeoutRef = useRef(null);
  const isVoteFeedbackActive = voteFeedback !== null;
  const isControlsSubmitting = isAdvancing || isVoteFeedbackActive;
  let t4;
  if ($[6] !== latestUsedAmount || $[7] !== maxRating) {
    t4 = latestUsedAmount === null ? null : normalizeQuickVoteAmount(latestUsedAmount, maxRating);
    $[6] = latestUsedAmount;
    $[7] = maxRating;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const normalizedLatestUsedAmount = t4;
  let t5;
  if ($[9] !== customValue || $[10] !== maxRating) {
    t5 = normalizeQuickVoteAmount(customValue, maxRating);
    $[9] = customValue;
    $[10] = maxRating;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const normalizedCustomAmount = t5;
  let t6;
  bb0: {
    if (maxRating <= 0) {
      let t7;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[13] !== maxRating || $[14] !== recentAmounts) {
      let t8;
      if ($[16] !== maxRating) {
        t8 = amount => normalizeQuickVoteAmount(amount, maxRating);
        $[16] = maxRating;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = Array.from(new Set(recentAmounts.map(t8).filter(_temp))).sort(_temp2);
      $[13] = maxRating;
      $[14] = recentAmounts;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = t7;
  }
  const visibleQuickAmounts = t6;
  const swipeVoteAmount = isCustomOpen ? normalizedCustomAmount : normalizedLatestUsedAmount ?? normalizedCustomAmount;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (barVoteTimeoutRef.current === null) {
        return;
      }
      globalThis.clearTimeout(barVoteTimeoutRef.current);
      barVoteTimeoutRef.current = null;
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const clearBarVoteTimeout = t7;
  let t8;
  if ($[19] !== activeDrop || $[20] !== submitVote) {
    t8 = amount_1 => {
      submitVote(activeDrop, amount_1).then(wasQueued => {
        if (!wasQueued) {
          setIsAdvancing(false);
          setVoteFeedback(null);
        }
      }).catch(() => {
        setIsAdvancing(false);
        setVoteFeedback(null);
      });
    };
    $[19] = activeDrop;
    $[20] = submitVote;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const queueVoteAmount = t8;
  let t10;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => () => {
      clearBarVoteTimeout();
    };
    t10 = [clearBarVoteTimeout];
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  if ($[24] !== isAdvancing || $[25] !== isVoteFeedbackActive || $[26] !== maxRating || $[27] !== queueVoteAmount) {
    t11 = (amount_2, source) => {
      if (isAdvancing || isVoteFeedbackActive) {
        return;
      }
      const normalizedAmount = normalizeQuickVoteAmount(amount_2, maxRating);
      if (normalizedAmount === null) {
        return;
      }
      setIsAdvancing(true);
      setVoteFeedback({
        amount: normalizedAmount,
        source
      });
      clearBarVoteTimeout();
      barVoteTimeoutRef.current = globalThis.setTimeout(() => {
        barVoteTimeoutRef.current = null;
        setIsAdvancing(true);
        queueVoteAmount(normalizedAmount);
      }, QUICK_VOTE_BAR_FEEDBACK_DURATION_MS);
    };
    $[24] = isAdvancing;
    $[25] = isVoteFeedbackActive;
    $[26] = maxRating;
    $[27] = queueVoteAmount;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleBarVoteAmount = t11;
  let t12;
  if ($[29] !== activeDrop || $[30] !== skipDrop) {
    t12 = () => {
      skipDrop(activeDrop);
    };
    $[29] = activeDrop;
    $[30] = skipDrop;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const queueSkip = t12;
  let t13;
  if ($[32] !== isControlsSubmitting || $[33] !== queueSkip) {
    t13 = () => {
      if (isControlsSubmitting) {
        return;
      }
      setIsAdvancing(true);
      queueSkip();
    };
    $[32] = isControlsSubmitting;
    $[33] = queueSkip;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleSkip = t13;
  let t14;
  if ($[35] !== isMobile || $[36] !== onClose || $[37] !== remainingCount) {
    t14 = isMobile && <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-white/5 tw-bg-black/40 tw-px-4 tw-pb-3 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-backdrop-blur-xl md:tw-hidden"><button type="button" onClick={onClose} data-autofocus="true" className="tw-inline-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.05] tw-text-iron-400 tw-shadow-inner tw-transition-colors active:tw-bg-white/10" aria-label="Close quick vote"><XMarkIcon className="tw-size-5 tw-shrink-0" /></button><div className="tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-px-3"><span className="tw-truncate tw-text-[13px] tw-font-bold tw-leading-tight tw-text-iron-300">{formatNumberWithCommas(remainingCount)} unexplored</span></div><div className="tw-size-10 tw-shrink-0" aria-hidden="true" /></div>;
    $[35] = isMobile;
    $[36] = onClose;
    $[37] = remainingCount;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== activeDrop || $[40] !== customValue || $[41] !== handleBarVoteAmount || $[42] !== handleSkip || $[43] !== isAdvancing || $[44] !== isControlsSubmitting || $[45] !== isCustomOpen || $[46] !== isMobile || $[47] !== isVoteFeedbackActive || $[48] !== normalizedLatestUsedAmount || $[49] !== queueSkip || $[50] !== queueVoteAmount || $[51] !== remainingCount || $[52] !== swipeVoteAmount || $[53] !== uncastPower || $[54] !== visibleQuickAmounts || $[55] !== voteFeedback?.amount || $[56] !== voteFeedback?.source || $[57] !== votingLabel) {
    t15 = isMobile ? <div className="tw-relative tw-z-10 tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden"><div className="tw-h-full"><MemesQuickVotePreview drop={activeDrop} isBusy={isAdvancing} isMobile={isMobile} remainingCount={remainingCount} swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} votingLabel={votingLabel} onAdvanceStart={() => {
          setIsAdvancing(true);
        }} onSkip={queueSkip} onVoteWithSwipe={() => {
          if (swipeVoteAmount === null) {
            setIsAdvancing(false);
            return;
          }
          queueVoteAmount(swipeVoteAmount);
        }} /></div><div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-20 md:tw-hidden"><MemesQuickVoteControls customValue={customValue} drop={activeDrop} isCustomOpen={isCustomOpen} isSubmitting={isControlsSubmitting} feedbackAmount={voteFeedback?.amount ?? null} feedbackSource={voteFeedback?.source ?? null} isVoteFeedbackActive={isVoteFeedbackActive} latestUsedAmount={normalizedLatestUsedAmount} remainingCount={remainingCount} quickAmounts={visibleQuickAmounts} uncastPower={uncastPower} votingLabel={votingLabel} onCustomChange={value => {
          if (value === "") {
            setCustomValue("");
            return;
          }
          setCustomValue(value.replace(/[^\d]/g, ""));
        }} onCustomSubmit={() => {
          handleBarVoteAmount(customValue, "custom-submit");
        }} onOpenCustom={() => {
          setIsCustomOpen(_temp3);
        }} onSkip={handleSkip} onVoteAmount={amount_3 => {
          handleBarVoteAmount(amount_3, "quick-amount");
        }} /></div></div> : <><div className="tw-min-h-0 tw-flex-1 md:tw-min-h-0 md:tw-border-y-0 md:tw-border-b-0 md:tw-border-l-0 md:tw-border-r md:tw-border-solid md:tw-border-white/10"><MemesQuickVotePreview drop={activeDrop} isBusy={isAdvancing} isMobile={isMobile} remainingCount={remainingCount} swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} votingLabel={votingLabel} onAdvanceStart={() => {
          setIsAdvancing(true);
        }} onSkip={queueSkip} onVoteWithSwipe={() => {
          if (swipeVoteAmount === null) {
            setIsAdvancing(false);
            return;
          }
          queueVoteAmount(swipeVoteAmount);
        }} /></div><div className="tw-shrink-0 md:tw-min-h-0 md:tw-self-stretch"><MemesQuickVoteControls customValue={customValue} drop={activeDrop} isCustomOpen={isCustomOpen} isSubmitting={isControlsSubmitting} feedbackAmount={voteFeedback?.amount ?? null} feedbackSource={voteFeedback?.source ?? null} isVoteFeedbackActive={isVoteFeedbackActive} latestUsedAmount={normalizedLatestUsedAmount} remainingCount={remainingCount} quickAmounts={visibleQuickAmounts} uncastPower={uncastPower} votingLabel={votingLabel} onCustomChange={value_0 => {
          if (value_0 === "") {
            setCustomValue("");
            return;
          }
          setCustomValue(value_0.replace(/[^\d]/g, ""));
        }} onCustomSubmit={() => {
          handleBarVoteAmount(customValue, "custom-submit");
        }} onOpenCustom={() => {
          setIsCustomOpen(_temp4);
        }} onSkip={handleSkip} onVoteAmount={amount_4 => {
          handleBarVoteAmount(amount_4, "quick-amount");
        }} /></div></>;
    $[39] = activeDrop;
    $[40] = customValue;
    $[41] = handleBarVoteAmount;
    $[42] = handleSkip;
    $[43] = isAdvancing;
    $[44] = isControlsSubmitting;
    $[45] = isCustomOpen;
    $[46] = isMobile;
    $[47] = isVoteFeedbackActive;
    $[48] = normalizedLatestUsedAmount;
    $[49] = queueSkip;
    $[50] = queueVoteAmount;
    $[51] = remainingCount;
    $[52] = swipeVoteAmount;
    $[53] = uncastPower;
    $[54] = visibleQuickAmounts;
    $[55] = voteFeedback?.amount;
    $[56] = voteFeedback?.source;
    $[57] = votingLabel;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t14 || $[60] !== t15) {
    t16 = <div className="tw-flex tw-h-full tw-flex-col md:tw-grid md:tw-min-h-0 md:tw-grid-cols-[minmax(0,1.22fr)_minmax(25rem,1fr)] md:tw-items-stretch">{t14}{t15}</div>;
    $[59] = t14;
    $[60] = t15;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
}
function _temp4(current_0) {
  return !current_0;
}
function _temp3(current) {
  return !current;
}
function _temp2(left, right) {
  return left - right;
}
function _temp(amount_0) {
  return amount_0 !== null;
}
function MemesQuickVoteDialogDoneState(t0) {
  const $ = _c(10);
  const {
    onClose
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-h-72 tw-w-72 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-primary-500/10 tw-blur-[84px]" />;
    t2 = <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-h-44 tw-w-44 -tw-translate-x-1/2 -tw-translate-y-[58%] tw-rounded-full tw-bg-white/5 tw-blur-[48px]" />;
    t3 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_46%)]" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-primary-500/10 tw-blur-2xl" />;
    t5 = <div className="tw-absolute tw-inset-3 tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.03] tw-backdrop-blur-md" />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-relative tw-mx-auto tw-mb-8 tw-flex tw-size-24 tw-items-center tw-justify-center md:tw-size-28">{t4}{t5}<div className="tw-relative tw-flex tw-size-20 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-black/50 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] md:tw-size-24"><CheckCircleIcon className="tw-size-10 tw-text-iron-100 md:tw-size-12" /></div></div>;
    t7 = <p className="tw-mb-3 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-white md:tw-text-[1.75rem]">You're all caught up</p>;
    t8 = <p className="tw-mx-auto tw-mb-8 tw-max-w-sm tw-text-sm tw-leading-6 tw-text-iron-400 md:tw-text-[15px]">No unrated memes are left in quick vote right now.</p>;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== onClose) {
    t9 = <div className="tw-relative tw-flex tw-min-h-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-[#0c0c0d]/95 tw-px-6 tw-py-10 md:tw-px-8 md:tw-py-12">{t1}{t2}{t3}<div className="tw-relative tw-z-10 tw-flex tw-w-full tw-max-w-md tw-flex-col tw-items-center tw-text-center">{t6}{t7}{t8}<button type="button" data-autofocus="true" onClick={onClose} className="tw-inline-flex tw-h-11 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-white/[0.05] tw-px-6 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition-all active:tw-scale-[0.98] desktop-hover:hover:tw-border-white/15 desktop-hover:hover:tw-bg-white/[0.08]">Close</button></div></div>;
    $[8] = onClose;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function MemesQuickVoteDialogErrorState(t0) {
  const $ = _c(4);
  const {
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-white">Couldn't load your queue</p>;
    t2 = <p className="tw-mb-4 tw-text-sm tw-text-iron-400">Quick vote couldn't reach the leaderboard. Try again.</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onRetry) {
    t3 = <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-py-8"><div className="tw-w-full tw-max-w-xl tw-rounded-[1.75rem] tw-border tw-border-solid tw-border-white/10 tw-bg-white/[0.03] tw-px-6 tw-py-10 tw-text-center tw-shadow-[0_24px_60px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm">{t1}{t2}<button type="button" onClick={onRetry} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-white/5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition-colors desktop-hover:hover:tw-border-white/20 desktop-hover:hover:tw-bg-white/10">Try again</button></div></div>;
    $[2] = onRetry;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export default function MemesQuickVoteDialog(t0) {
  const $ = _c(44);
  const {
    isOpen,
    sessionId,
    onClose
  } = t0;
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const previousBodyOverflowRef = useRef("");
  const isMobile = useMediaQuery(QUICK_VOTE_MOBILE_QUERY);
  let t1;
  if ($[0] !== isOpen || $[1] !== sessionId) {
    t1 = {
      enabled: isOpen,
      sessionId
    };
    $[0] = isOpen;
    $[1] = sessionId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    activeDrop,
    hasDiscoveryError,
    isExhausted,
    isLoading,
    latestUsedAmount,
    recentAmounts,
    remainingCount,
    retryDiscovery,
    submitVote,
    skipDrop,
    uncastPower,
    votingLabel
  } = useMemesQuickVoteQueue(t1);
  let t2;
  let t3;
  if ($[3] !== isOpen) {
    t2 = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      if (!isOpen) {
        if (dialog.open) {
          dialog.close();
        }
        document.body.style.overflow = previousBodyOverflowRef.current;
        previouslyFocusedElementRef.current?.focus();
        return;
      }
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      previousBodyOverflowRef.current = document.body.style.overflow;
      if (!dialog.open) {
        dialog.showModal();
      }
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        const autofocusTarget = dialog.querySelector("[data-autofocus='true']");
        autofocusTarget?.focus();
      });
      return () => {
        document.body.style.overflow = previousBodyOverflowRef.current;
      };
    };
    t3 = [isOpen];
    $[3] = isOpen;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== onClose) {
    t4 = () => {
      const dialog_0 = dialogRef.current;
      if (!dialog_0) {
        return;
      }
      const handleCancel = event => {
        event.preventDefault();
        onClose();
      };
      dialog_0.addEventListener("cancel", handleCancel);
      return () => {
        dialog_0.removeEventListener("cancel", handleCancel);
      };
    };
    t5 = [onClose];
    $[6] = onClose;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let dialogBody;
  if (isExhausted) {
    let t6;
    if ($[9] !== onClose) {
      t6 = <MemesQuickVoteDialogDoneState onClose={onClose} />;
      $[9] = onClose;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    dialogBody = t6;
  } else {
    if (!activeDrop && hasDiscoveryError) {
      let t6;
      if ($[11] !== retryDiscovery) {
        t6 = <MemesQuickVoteDialogErrorState onRetry={retryDiscovery} />;
        $[11] = retryDiscovery;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      dialogBody = t6;
    } else {
      if (!activeDrop && isLoading) {
        let t6;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <MemesQuickVoteDialogSkeleton />;
          $[13] = t6;
        } else {
          t6 = $[13];
        }
        dialogBody = t6;
      } else {
        if (!activeDrop) {
          let t6;
          if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
            t6 = <MemesQuickVoteDialogSkeleton />;
            $[14] = t6;
          } else {
            t6 = $[14];
          }
          dialogBody = t6;
        } else {
          const t6 = `${sessionId}:${activeDrop.serial_no}:${isOpen ? "open" : "closed"}`;
          let t7;
          if ($[15] !== activeDrop || $[16] !== isMobile || $[17] !== latestUsedAmount || $[18] !== onClose || $[19] !== recentAmounts || $[20] !== remainingCount || $[21] !== skipDrop || $[22] !== submitVote || $[23] !== t6 || $[24] !== uncastPower || $[25] !== votingLabel) {
            t7 = <MemesQuickVoteDialogContent key={t6} activeDrop={activeDrop} isMobile={isMobile} latestUsedAmount={latestUsedAmount} onClose={onClose} remainingCount={remainingCount} recentAmounts={recentAmounts} submitVote={submitVote} skipDrop={skipDrop} uncastPower={uncastPower} votingLabel={votingLabel} />;
            $[15] = activeDrop;
            $[16] = isMobile;
            $[17] = latestUsedAmount;
            $[18] = onClose;
            $[19] = recentAmounts;
            $[20] = remainingCount;
            $[21] = skipDrop;
            $[22] = submitVote;
            $[23] = t6;
            $[24] = uncastPower;
            $[25] = votingLabel;
            $[26] = t7;
          } else {
            t7 = $[26];
          }
          dialogBody = t7;
        }
      }
    }
  }
  let t6;
  if ($[27] !== onClose) {
    t6 = event_0 => {
      if (event_0.target !== event_0.currentTarget) {
        return;
      }
      onClose();
    };
    $[27] = onClose;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const t7 = isMobile ? undefined : "true";
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <XMarkIcon className="tw-size-5 tw-shrink-0" />;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== onClose || $[31] !== t7) {
    t9 = <button type="button" data-autofocus={t7} onClick={onClose} className="tw-absolute tw-right-4 tw-top-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-z-20 tw-hidden tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.05] tw-text-iron-400 tw-shadow-inner tw-backdrop-blur-md tw-transition-colors active:tw-bg-white/10 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-text-white md:tw-right-6 md:tw-top-6 md:tw-inline-flex" aria-label="Close quick vote">{t8}</button>;
    $[30] = onClose;
    $[31] = t7;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== dialogBody) {
    t10 = <div className="tw-relative tw-z-10 tw-min-h-0 tw-flex-1 tw-overflow-hidden md:tw-p-0">{dialogBody}</div>;
    $[33] = dialogBody;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9) {
    t11 = <div className="tw-relative tw-flex tw-h-full tw-max-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-bg-[#0a0a0a] tw-shadow-[0_0_80px_rgba(0,0,0,0.8)] md:tw-h-[38rem] md:tw-max-h-[min(calc(100vh-3rem),38rem)] md:tw-max-w-[68rem] md:tw-rounded-2xl md:tw-border md:tw-border-solid md:tw-border-white/10">{t9}{t10}</div>;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11 || $[39] !== t6) {
    t12 = <div className="tw-flex tw-h-full tw-w-full tw-items-stretch tw-justify-center tw-bg-gray-600 tw-bg-opacity-50 tw-backdrop-blur-[1px] md:tw-items-center md:tw-p-6" onClick={t6}>{t11}</div>;
    $[38] = t11;
    $[39] = t6;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== sessionId || $[42] !== t12) {
    t13 = <dialog ref={dialogRef} data-session-id={sessionId} className="tailwind-scope tw-fixed tw-inset-0 tw-m-0 tw-h-[100dvh] tw-max-h-[100dvh] tw-w-screen tw-max-w-none tw-overflow-hidden tw-border-none tw-bg-transparent tw-p-0" aria-label="Memes quick vote">{t12}</dialog>;
    $[41] = sessionId;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}